import { InfraIoError } from './errors';
import { openPopup } from './popup';
import { preloadCheckoutOrigin } from './preload';
import { openRedirect } from './redirect';
import type { CheckoutOptions, InfraIoInstance } from './types';

export class InfraIo implements InfraIoInstance {
  private activeClose: (() => void) | null = null;

  constructor(
    private readonly publicKey: string,
    private readonly checkoutBaseURL: string,
  ) {
    if (typeof document !== 'undefined') {
      preloadCheckoutOrigin(this.checkoutBaseURL);
    }
  }

  checkout(options: CheckoutOptions): void {
    if (!options.sessionId) {
      throw new InfraIoError('invalid_request_error', 'Missing sessionId in checkout options.');
    }

    const mode = options.mode ?? 'popup';
    if (mode === 'redirect') {
      openRedirect(this.publicKey, this.checkoutBaseURL, options);
      return;
    }

    // Reject concurrent popup open to prevent stacking overlays on double-click,
    // React strict-mode double invoke, etc. Merchant can call .close() first.
    if (this.activeClose) {
      throw new InfraIoError(
        'already_open_error',
        'A checkout popup is already open. Call .close() before opening a new one.',
      );
    }

    // Wrap merchant callbacks so `activeClose` is cleared on any dismissal path.
    // We do this before passing to openPopup so the wrappers are what gets invoked.
    const wrapped: CheckoutOptions = {
      ...options,
      onSuccess: (data) => {
        this.activeClose = null;
        options.onSuccess?.(data);
      },
      onCancel: () => {
        this.activeClose = null;
        options.onCancel?.();
      },
      onError: (err) => {
        this.activeClose = null;
        options.onError?.(err);
      },
    };

    this.activeClose = openPopup(this.publicKey, this.checkoutBaseURL, wrapped);
  }

  close(): void {
    if (this.activeClose) {
      this.activeClose();
      this.activeClose = null;
    }
  }
}
